// ============================================================================
// GESTIMMONY — Activation d'un accès collaborateur SCI (magic link, lecture seule)
// ============================================================================
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Users, Eye } from "lucide-react";

export default function MemberActivatePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/account/members/invite/${token}`)
      .then(({ data }) => setInfo(data))
      .catch((e) => setError(e.response?.data?.detail || "Lien invalide ou expiré"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Les mots de passe ne correspondent pas"); return; }
    setBusy(true);
    try {
      await api.post("/account/members/activate", { token, password });
      toast.success("Accès activé — bienvenue !");
      try { await refresh(); } catch {}
      navigate("/app");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="member-activate-page">
      <Card className="w-full max-w-md p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center">
            <Users className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-xl">Accès collaborateur</h1>
            <p className="text-sm text-slate-500">Choisissez votre mot de passe pour accéder au compte</p>
          </div>
        </div>

        {error && (
          <p className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="member-activate-error">
            {error}
          </p>
        )}

        {!error && info && (
          <>
            <div className="rounded-lg bg-[hsl(var(--accent))]/50 border border-stone-200 px-4 py-3 text-sm text-slate-600 mb-5">
              <div className="flex items-center gap-2 text-slate-800 font-medium">
                <Eye className="h-4 w-4 text-teal-700" /> Accès en lecture seule
              </div>
              <p className="mt-1">
                {info.owner_name ? <b>{info.owner_name}</b> : "Le titulaire du compte"} vous invite à
                consulter ses biens, locataires, baux, quittances et finances. Vous ne pourrez rien modifier.
              </p>
              <p className="mt-2 text-xs text-slate-500">Compte : {info.email}</p>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label>Nouveau mot de passe (6 caractères minimum)</Label>
                <Input data-testid="member-activate-pwd" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Confirmer</Label>
                <Input data-testid="member-activate-confirm" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1" />
              </div>
              <Button data-testid="member-activate-submit" type="submit" disabled={busy} className="w-full bg-[hsl(var(--primary))]">
                {busy ? "Activation…" : "Activer et accéder au compte"}
              </Button>
            </form>
          </>
        )}

        {!error && !info && <p className="text-sm text-slate-400 py-4">Vérification du lien…</p>}
      </Card>
    </div>
  );
}
